/**
 * پردازش داده‌های منوهای غذایی مطابق فرانت قدیم
 * این فایل داده‌های خام از API را به فرمتی که در تولید منو استفاده می‌شود تبدیل می‌کند
 */

import { parseNumber } from './roundingUtils';

/**
 * پردازش داده‌های منوها - مطابق فرانت قدیم
 * @param {Array} rawData - داده‌های خام از API
 * @returns {Array} - داده‌های فرمت شده
 */
export const processFormattedData = (rawData) => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    console.warn('داده‌های ورودی نامعتبر است');
    return [];
  }

  return rawData.map((menu) => {
    // پردازش fold_nappes (سفره‌چین‌ها)
    const foldNappes = [];
    for (let i = 1; i <= 5; i++) {
      const foldNappeName = menu[`fold_nappe${i}`];
      if (foldNappeName) {
        const percentage = parseNumber(menu[`percentage_fold_nappe${i}`]);
        const calories = parseNumber(menu[`calories_fold_nappe${i}`]);
        const roundingMethod = menu[`rounding_method_fold_nappe${i}`] || "round_half";
        
        if (percentage === 0 || calories === 0) {
          console.warn(`سفره‌چین ${foldNappeName} دارای درصد یا کالری نامعتبر است:`, {
            percentage,
            calories,
          });
          continue;
        }
        
        foldNappes.push({
          name: foldNappeName,
          arrangement: menu[`arrangement_fold_nappe${i}`] || "",
          percentage: percentage,
          calories: calories,
          rounding_method: roundingMethod,
        });
      }
    }

    // پردازش items (آیتم‌ها)
    const items = [];
    for (let i = 1; i <= 5; i++) {
      if (menu[`item${i}`]) {
        const percentage = parseNumber(menu[`percentage_item${i}`]);
        const calories = parseNumber(menu[`calories_item${i}`]);
        
        if (percentage === 0 || calories === 0) {
          console.warn(`آیتم ${menu[`item${i}`]} دارای درصد یا کالری نامعتبر است:`, {
            percentage,
            calories,
          });
          continue;
        }
        
        items.push({
          name: menu[`item${i}`],
          calories: calories,
          percentage: percentage,
          rounding_method: menu[`rounding_method_item${i}`] || "round_half",
        });
      }
    }

    // برگرداندن object فرمت شده (مطابق فرانت قدیم)
    const formattedMenu = {
      id: menu.id,
      Breakfast_Name: menu.name || "",
      text: menu.text || "",
      items: items,
      fold_nappes: foldNappes,
      meal: menu.meal || "",
      portion_type: menu.portion_type || "",
      form_type: menu.form_type,
      selected_food: menu.selected_food || null,
      selected_fold_nappe: menu.selected_fold_nappe || "",
      group_number: menu.group_number || null,
      group_title: menu.group_title || null,
      group_repetition_count: menu.group_repetition_count || null
    };
    
    // دیباگ: بررسی meal برای منوهای مشکوک (مثلاً منوهایی که meal آن‌ها با محتوای text مطابقت ندارد)
    if (formattedMenu.meal && formattedMenu.text) {
      const meal = String(formattedMenu.meal).trim();
      const text = String(formattedMenu.text).trim();
      
      // بررسی منوهای مشکوک: اگر meal "میان‌وعده عصر" است اما text شامل "مرغ سوخاری" یا "ناهار" است
      if (meal === 'میان‌وعده عصر' && (text.includes('مرغ سوخاری') || text.includes('ناهار') || text.includes('قطعه مرغ') || text.includes('خورشت') || text.includes('برنج'))) {
        console.warn(`⚠️ [foodDataProcessor] منوی مشکوک یافت شد (میان‌وعده عصر با محتوای ناهار):`, {
          id: formattedMenu.id,
          meal: meal,
          text: text.substring(0, 100),
          form_type: formattedMenu.form_type,
          selected_food: formattedMenu.selected_food
        });
      }
      
      // بررسی منوهای مشکوک: اگر meal "ناهار" است اما text شامل "شیر" یا "میان‌وعده" است
      if (meal === 'ناهار' && (text.includes('شیر') || text.includes('میان‌وعده') || text.includes('خرما'))) {
        console.warn(`⚠️ [foodDataProcessor] منوی مشکوک یافت شد (ناهار با محتوای میان‌وعده):`, {
          id: formattedMenu.id,
          meal: meal,
          text: text.substring(0, 100),
          form_type: formattedMenu.form_type,
          selected_food: formattedMenu.selected_food
        });
      }
    }
    
    return formattedMenu;
  });
};

/**
 * محاسبه کالری‌های وعده‌های غذایی بر اساس DailyRecord
 * مطابق فرانت قدیم
 * @param {Object} params - پارامترهای محاسبه
 * @returns {Object} - کالری‌های محاسبه شده
 */
export const calculateMealCaloriesForDailyRecord = ({
  dailyRecord,
  outputInformation,
  dietOrder2,
  dietOrder,
  percentBreakfast,
  percentAfterBreakfast,
  percentMorningSnack,
  percentLunch,
  percentAfterLunch,
  percentEveningSnack,
  percentDinner,
  percentNightSnack
}) => {
  let breakfastcal, afterbreakfastandlunchcal, morningsnackcal, lunchCal;
  let afterlunchanddinnerCal, eveningsnackcal, dinnerCal, nightsnackcal;
  let quotaCal1, quotaCal2, quotaCal3;

  // بررسی شرط اول: weight_loss_diet_calories1 و weight_loss_diet_calories2
  if (
    (outputInformation.weight_loss_diet_calories1 == 0 && outputInformation.weight_loss_diet_calories2 == 0) ||
    (outputInformation.weight_loss_diet_calories1 == null && outputInformation.weight_loss_diet_calories2 == null)
  ) {
    // حالت اول: استفاده از weight_loss_diet_calories
    if (dietOrder == 'increase') {
      // رژیم افزایش وزن
      breakfastcal = Math.round((percentBreakfast / 100) * outputInformation.weight_loss_diet_calories);
      afterbreakfastandlunchcal = Math.round((percentAfterBreakfast / 100) * outputInformation.weight_loss_diet_calories);
      morningsnackcal = Math.round((percentMorningSnack / 100) * outputInformation.weight_loss_diet_calories);
      lunchCal = Math.round((percentLunch / 100) * outputInformation.weight_loss_diet_calories);
      afterlunchanddinnerCal = Math.round((percentAfterLunch / 100) * outputInformation.weight_loss_diet_calories);
      eveningsnackcal = Math.round((percentEveningSnack / 100) * outputInformation.weight_loss_diet_calories);
      dinnerCal = Math.round((percentDinner / 100) * outputInformation.weight_loss_diet_calories);
      nightsnackcal = Math.round((percentNightSnack / 100) * outputInformation.weight_loss_diet_calories);
    } else {
      // سایر رژیم‌ها
      lunchCal = Math.round((percentLunch / 100) * outputInformation.weight_loss_diet_calories);
      breakfastcal = Math.round((percentBreakfast / 100) * outputInformation.weight_loss_diet_calories);
      morningsnackcal = Math.round((percentMorningSnack / 100) * outputInformation.weight_loss_diet_calories);
      eveningsnackcal = Math.round((percentEveningSnack / 100) * outputInformation.weight_loss_diet_calories);
      dinnerCal = Math.round((percentDinner / 100) * outputInformation.weight_loss_diet_calories);
      nightsnackcal = Math.round((percentNightSnack / 100) * outputInformation.weight_loss_diet_calories);
    }

    // محاسبه سهمیه‌ها
    quotaCal1 = Math.round((dietOrder2.percent_quota1 / 100) * outputInformation.daily_quota);
    quotaCal2 = Math.round((dietOrder2.percent_quota2 / 100) * outputInformation.daily_quota);
    quotaCal3 = Math.round((dietOrder2.percent_quota3 / 100) * outputInformation.daily_quota);
  } else {
    // حالت دوم: استفاده از daysInFirstArray و daysInSecondArray
    const currentDateToSearch = dailyRecord.date;
    const daysInFirstArray = JSON.parse(outputInformation.daysInFirstArray || '[]');
    const daysInSecondArray = JSON.parse(outputInformation.daysInSecondArray || '[]');
    
    const foundDate = daysInFirstArray.includes(currentDateToSearch);
    const foundDate2 = daysInSecondArray.includes(currentDateToSearch);

    if (foundDate) {
      // روزهای اول (weight_loss_diet_calories1)
      lunchCal = Math.round((percentLunch / 100) * outputInformation.weight_loss_diet_calories1);
      breakfastcal = Math.round((percentBreakfast / 100) * outputInformation.weight_loss_diet_calories1);
      eveningsnackcal = Math.round((percentEveningSnack / 100) * outputInformation.weight_loss_diet_calories1);
      morningsnackcal = Math.round((percentMorningSnack / 100) * outputInformation.weight_loss_diet_calories1);
      dinnerCal = Math.round((percentDinner / 100) * outputInformation.weight_loss_diet_calories1);
      nightsnackcal = Math.round((percentNightSnack / 100) * outputInformation.weight_loss_diet_calories1);

      quotaCal1 = outputInformation.daily_quota1;
      quotaCal2 = 0;
      quotaCal3 = 0;
    } else if (foundDate2) {
      // روزهای دوم (weight_loss_diet_calories2)
      lunchCal = Math.round((percentLunch / 100) * outputInformation.weight_loss_diet_calories2);
      breakfastcal = Math.round((percentBreakfast / 100) * outputInformation.weight_loss_diet_calories2);
      eveningsnackcal = Math.round((percentEveningSnack / 100) * outputInformation.weight_loss_diet_calories2);
      morningsnackcal = Math.round((percentMorningSnack / 100) * outputInformation.weight_loss_diet_calories2);
      dinnerCal = Math.round((percentDinner / 100) * outputInformation.weight_loss_diet_calories2);
      nightsnackcal = Math.round((percentNightSnack / 100) * outputInformation.weight_loss_diet_calories2);

      quotaCal1 = Math.round((dietOrder2.percent_quota1 / 100) * outputInformation.daily_quota2);
      quotaCal2 = Math.round((dietOrder2.percent_quota2 / 100) * outputInformation.daily_quota2);
      quotaCal3 = Math.round((dietOrder2.percent_quota3 / 100) * outputInformation.daily_quota2);
    }
  }

  return {
    breakfastcal,
    afterbreakfastandlunchcal,
    morningsnackcal,
    lunchCal,
    afterlunchanddinnerCal,
    eveningsnackcal,
    dinnerCal,
    nightsnackcal,
    quotaCal1,
    quotaCal2,
    quotaCal3
  };
};

